import { create } from 'zustand';
import { NetworkSimulator } from './networkSimulator';

const simulator = new NetworkSimulator();

// Load saved state from localStorage
const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem('netmappr-network');
    if (saved) {
      const data = JSON.parse(saved);
      // Restore devices to simulator
      data.devices.forEach(device => {
        simulator.addDevice(device);
      });
      // Restore connections to simulator
      data.connections.forEach(conn => {
        simulator.addConnection(conn.device1, conn.port1, conn.device2, conn.port2);
      });
      return data;
    }
  } catch (error) {
    console.error('Failed to load saved network:', error);
  }
  return { devices: [], connections: [] };
};

const savedState = loadFromStorage();

const useStore = create((set, get) => ({
  // Network state
  devices: savedState.devices,
  connections: savedState.connections,
  selectedDevice: null,
  deviceCounter: savedState.devices.length,

  // Terminal state
  terminalOutput: [],
  commandHistory: [],
  historyIndex: -1,

  // UI state
  connectionMode: false,
  connectionStart: null,
  panelOpen: false,
  panelType: 'terminal',
  
  // Lesson state
  currentLesson: null,
  lessonMode: false,

  // Initialize with simulator
  simulator,

  // Save to localStorage
  saveToStorage: () => {
    const state = get();
    try {
      const data = {
        devices: state.devices,
        connections: state.connections,
      };
      localStorage.setItem('netmappr-network', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save network:', error);
    }
  },

  // Panel control
  setPanelOpen: (open) => {
    set({ panelOpen: open });
  },

  setPanelType: (type) => {
    set({ panelType: type, panelOpen: true });
  },

  // Lesson control
  loadLesson: (lesson) => {
    // Clear existing network
    simulator.devices.clear();
    simulator.connections.clear();
    
    // Load lesson devices
    const deviceMap = new Map();
    const loadedDevices = [];
    
    lesson.topology.devices.forEach((lessonDevice, index) => {
      const id = `device_${Date.now()}_${index}`;
      
      // Handle router interfaces
      let interfaces = [];
      if (lessonDevice.type === 'router') {
        const portCount = getPortCount('router');
        
        if (lessonDevice.interfaces) {
          interfaces = lessonDevice.interfaces;
        } else {
          for (let i = 0; i < portCount; i++) {
            interfaces.push({
              port: i,
              ip: '',
              subnet: '255.255.255.0',
            });
          }
        }
      }
      
      const device = {
        id,
        type: lessonDevice.type,
        name: lessonDevice.name,
        x: lessonDevice.x,
        y: lessonDevice.y,
        powered: lessonDevice.powered !== undefined ? lessonDevice.powered : true,
        ip: lessonDevice.ip || '',
        subnet: lessonDevice.subnet || '255.255.255.0',
        gateway: lessonDevice.gateway || '',
        dns: lessonDevice.dns || '8.8.8.8',
        mac: generateMAC(),
        ports: getPortCount(lessonDevice.type),
        dhcpEnabled: lessonDevice.type === 'server' || lessonDevice.type === 'router',
        networkTag: lessonDevice.networkTag || '',
        interfaces: interfaces,
      };
      
      simulator.addDevice(device);
      deviceMap.set(lessonDevice.name, device.id);
      loadedDevices.push(device);
    });

    // Load lesson connections
    const connections = [];
    if (lesson.topology.connections && Array.isArray(lesson.topology.connections)) {
      lesson.topology.connections.forEach((conn, index) => {
        const device1Id = deviceMap.get(conn.from);
        const device2Id = deviceMap.get(conn.to);
        
        if (!device1Id || !device2Id) {
          console.warn(`Connection skipped: ${conn.from} → ${conn.to}`);
          return;
        }
        
        const connection = {
          id: `conn_${Date.now()}_${index}`,
          device1: device1Id,
          port1: conn.fromPort || 0,
          device2: device2Id,
          port2: conn.toPort || 0,
          cableType: 'ethernet',
        };

        simulator.addConnection(device1Id, conn.fromPort || 0, device2Id, conn.toPort || 0);
        connections.push(connection);
      });
    }

    set({
      devices: loadedDevices,
      connections: connections,
      currentLesson: lesson,
      lessonMode: true,
      deviceCounter: loadedDevices.length,
      selectedDevice: null,
      terminalOutput: [],
      panelType: 'lesson',
      panelOpen: true,
    });
  },

  exitLesson: () => {
    set({ 
      currentLesson: null, 
      lessonMode: false,
      panelType: 'terminal'
    });
  },

  // Device management
  addDevice: (type, x, y) => {
    const state = get();
    const id = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Setup router interfaces if router - CREATE ALL 4 PORTS
    let interfaces = [];
    if (type === 'router') {
      const portCount = getPortCount(type); // 4 ports
      for (let i = 0; i < portCount; i++) {
        interfaces.push({
          port: i,
          ip: '',
          subnet: '255.255.255.0',
        });
      }
    }
    
    const device = {
      id,
      type,
      name: `${type.toUpperCase()}-${state.deviceCounter + 1}`,
      x,
      y,
      powered: true,
      ip: type === 'router' ? '' : generateIP(),
      subnet: '255.255.255.0',
      gateway: '',
      dns: '8.8.8.8',
      mac: generateMAC(),
      ports: getPortCount(type),
      dhcpEnabled: type === 'server' || type === 'router',
      networkTag: '',
      interfaces: interfaces,
    };

    simulator.addDevice(device);

    set({
      devices: [...state.devices, device],
      deviceCounter: state.deviceCounter + 1,
    });

    setTimeout(() => get().saveToStorage(), 100);

    return device;
  },

  updateDevice: (deviceId, updates) => {
    set(state => {
      const newDevices = state.devices.map(d => {
        if (d.id === deviceId) {
          const updated = { ...d, ...updates };
          // Update in simulator
          simulator.devices.set(deviceId, updated);
          // Update selected device if it's this one
          if (state.selectedDevice?.id === deviceId) {
            state.selectedDevice = updated;
          }
          return updated;
        }
        return d;
      });

      return {
        devices: newDevices,
        selectedDevice: state.selectedDevice?.id === deviceId 
          ? newDevices.find(d => d.id === deviceId)
          : state.selectedDevice
      };
    });

    setTimeout(() => get().saveToStorage(), 100);
  },

  removeDevice: (deviceId) => {
    simulator.removeDevice(deviceId);
    set(state => ({
      devices: state.devices.filter(d => d.id !== deviceId),
      connections: state.connections.filter(
        c => c.device1 !== deviceId && c.device2 !== deviceId
      ),
      selectedDevice: state.selectedDevice?.id === deviceId ? null : state.selectedDevice,
    }));

    setTimeout(() => get().saveToStorage(), 100);
  },

  updateDevicePosition: (deviceId, x, y) => {
    set(state => ({
      devices: state.devices.map(d =>
        d.id === deviceId ? { ...d, x, y } : d
      ),
    }));

    setTimeout(() => get().saveToStorage(), 500);
  },

  selectDevice: (device) => {
    set({ selectedDevice: device });
  },

  // Connection management
  addConnection: (device1Id, port1, device2Id, port2) => {
    const state = get();
    const id = `conn_${Date.now()}`;

    const connection = {
      id,
      device1: device1Id,
      port1,
      device2: device2Id,
      port2,
      cableType: 'ethernet',
    };

    simulator.addConnection(device1Id, port1, device2Id, port2);

    set({
      connections: [...state.connections, connection],
      connectionMode: false,
      connectionStart: null,
    });

    setTimeout(() => get().saveToStorage(), 100);
  },

  removeConnection: (connectionId) => {
    const state = get();
    const connection = state.connections.find(c => c.id === connectionId);
    
    if (connection) {
      // Remove from simulator
      simulator.removeConnection(connection.device1, connection.port1, connection.device2, connection.port2);
    }
    
    set(state => ({
      connections: state.connections.filter(c => c.id !== connectionId),
    }));

    setTimeout(() => get().saveToStorage(), 100);
  },

  startConnection: (deviceId, port) => {
    set({
      connectionMode: true,
      connectionStart: { deviceId, port },
    });
  },

  cancelConnection: () => {
    set({
      connectionMode: false,
      connectionStart: null,
    });
  },

  // Terminal management
  addTerminalOutput: (text, type = 'info') => {
    set(state => ({
      terminalOutput: [
        ...state.terminalOutput,
        { text, type, timestamp: Date.now() },
      ],
    }));
  },

  clearTerminal: () => {
    set({ terminalOutput: [] });
  },

  addToHistory: (command) => {
    set(state => ({
      commandHistory: [...state.commandHistory, command],
      historyIndex: -1,
    }));
  },

  navigateHistory: (direction) => {
    const state = get();
    const { commandHistory, historyIndex } = state;
    
    if (direction === 'up') {
      const newIndex = historyIndex + 1;
      if (newIndex < commandHistory.length) {
        return commandHistory[commandHistory.length - 1 - newIndex];
      }
    } else if (direction === 'down') {
      const newIndex = historyIndex - 1;
      if (newIndex >= 0) {
        return commandHistory[commandHistory.length - 1 - newIndex];
      }
      return '';
    }
    return null;
  },

  // Clear all
  clearAll: () => {
    simulator.devices.clear();
    simulator.connections.clear();
    
    set({
      devices: [],
      connections: [],
      selectedDevice: null,
      deviceCounter: 0,
      terminalOutput: [],
      connectionMode: false,
      connectionStart: null,
      panelOpen: false,
      currentLesson: null,
      lessonMode: false,
    });

    localStorage.removeItem('netmappr-network');
  },
}));

// Helper functions
function generateIP() {
  return `192.168.1.${Math.floor(Math.random() * 200 + 10)}`;
}

function generateMAC() {
  return Array.from({ length: 6 }, () =>
    Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, '0')
  )
    .join(':')
    .toUpperCase();
}

function getPortCount(type) {
  const ports = {
    pc: 1,
    laptop: 1,
    server: 2,
    router: 4,  // 4 ports for router
    switch: 8,
    hub: 4,
  };
  return ports[type] || 1;
}

export default useStore;
